import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  showSuccessToast,
  showErrorToast,
} from "../../utils/notificationUtils";
import { notificationsAPI, socialAPI, petGuardianshipAPI } from "../../services/api";
import notificationService from "../../services/notificationService";
import { usePostModal } from "../../contexts/PostModalContext";
import { USER_TYPES } from "../../constants/userTypes";
import { handleNotificationRouting, isNotificationClickable } from "../../utils/notificationRouting";
import "./NotificationDropdown.css";

const NotificationDropdown = ({
  isOpen,
  onClose,
  userType,
  colors,
  onRequestHandled,
  updateNotificationCount,
  highlightNotificationId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { openPostModal } = usePostModal();

  // Get clinicId from URL params or try to extract from current path
  const getClinicId = () => {
    if (params?.clinicId) return params.clinicId;
    const pathMatch = window.location.pathname.match(/\/medical\/clinic\/(\d+)/);
    if (pathMatch) return pathMatch[1];
    try {
      const userData = localStorage.getItem('user_data');
      if (userData && userData !== 'undefined' && userData !== 'null') {
        const parsed = JSON.parse(userData);
        return parsed.clinic_id;
      }
    } catch (e) {
      // Ignore errors
    }
    return null;
  };
  const [notifications, setNotifications] = useState([]);
  const [pendingFollowRequests, setPendingFollowRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [processingRequest, setProcessingRequest] = useState(null); // Track which notification is being processed

  // Ref for the interval
  const intervalRef = useRef(null);
  const listRef = useRef(null);
  const highlightedNotificationRef = useRef(null);

  // Color schemes based on user type
  const getColorScheme = (userType) => {
    const type = userType?.toUpperCase();
    switch (type) {
      case "PET_GUARDIAN":
        return { primary: "#8548A8", hover: "#7A4397" };
      case "VETERINARIAN":
      case "VET":
        return { primary: "#00D4B3", hover: "#00C4A7" };
      case "SERVICE_PROVIDER":
      case "BUSINESS":
        return { primary: "#D65745", hover: "#C7493A" };
      default:
        return { primary: "#8548A8", hover: "#7A4397" };
    }
  };


  // Handle follow request approval/rejection
  const handleFollowRequest = async (requestId, action, notificationId = null) => {
    try {
      setError(null);
      setProcessingRequest(notificationId || requestId);

      // Call the appropriate API method
      const response = action === 'approve'
        ? await socialAPI.acceptConnectionRequest(requestId)
        : await socialAPI.rejectConnectionRequest(requestId);


      // Update the notifications state - mark as processed
      setNotifications(prevNotifications => {
        return prevNotifications.map(notif => {
          // Check if this is the notification we're processing
          const isTargetNotification = notificationId
            ? notif.id === notificationId
            : (() => {
              const senderId = notif.sender_id ||
                notif.parsedData?.sender_id ||
                notif.parsedData?.user_id ||
                notif.sender_Data?.id ||
                notif.parsedData?.content_id ||
                notif.target_id;
              return senderId === requestId;
            })();

          if (isTargetNotification) {
            // Mark notification as processed and update the text
            return {
              ...notif,
              processed: true,
              processedAction: action,
              mainText: action === 'approve'
                ? t("navbar.Buddy_Request_Accepted", { name: notif.senderInfo || t("navbar.Someone") })
                : t("navbar.Buddy_Request_Rejected", { name: notif.senderInfo || t("navbar.Someone") })
            };
          }
          return notif;
        });
      });

      setPendingFollowRequests(prev => prev.filter(req => (req.user_id || req.id) !== requestId));

      // Remove processed notification after showing the status for 2 seconds
      setTimeout(() => {
        setNotifications(current =>
          current.filter(notif => {
            if (notificationId) {
              return notif.id !== notificationId;
            }
            const senderId = notif.sender_id ||
              notif.parsedData?.sender_id ||
              notif.parsedData?.user_id ||
              notif.sender_Data?.id ||
              notif.parsedData?.content_id ||
              notif.target_id;
            return senderId !== requestId;
          })
        );
      }, 2000);

      // Notify parent component to refresh count
      if (onRequestHandled) {
        onRequestHandled();
      }
    } catch (error) {
      console.error(`Error ${action}ing follow request:`, error);
      showErrorToast(t("navbar.Failed_To_Process_Request"));
    } finally {
      setProcessingRequest(null);
    }
  };

  // Handle clinic invitation acceptance/rejection
  const handleClinicInvitation = async (invitationId, action, notificationId = null) => {
    try {
      setError(null);
      setProcessingRequest(notificationId || invitationId);

      const apiAction = action === 'accept' ? 'accept' : 'reject';
      await clinicAPI.respondToInvitation(invitationId, apiAction);

      // Update local state - mark as processed
      setNotifications(prevNotifications => {
        return prevNotifications.map(notif => {
          if (notif.id === notificationId) {
            return {
              ...notif,
              processed: true,
              processedAction: action,
              mainText: action === 'accept'
                ? t("navbar.Clinic_Invitation_Accepted")
                : t("navbar.Clinic_Invitation_Rejected")
            };
          }
          return notif;
        });
      });

      // Show success toast
      const actionText = action === "accept" ? t("navbar.Accepted") : t("navbar.Rejected");
      showSuccessToast(t("navbar.Clinic_Invitation_Processed", { action: actionText }));

      // Remove processed notification after 2 seconds
      setTimeout(() => {
        setNotifications(current => current.filter(notif => notif.id !== notificationId));
      }, 2000);

      // Notify parent component to refresh count
      if (onRequestHandled) {
        onRequestHandled();
      }
    } catch (error) {
      console.error(`Error ${action}ing clinic invitation:`, error);
      showErrorToast(t("navbar.Failed_To_Process_Clinic_Invitation"));
    } finally {
      setProcessingRequest(null);
    }
  };

  // Handle guardianship invitation response
  const handleGuardianshipResponse = async (requestId, action, notificationId = null) => {
    try {
      setError(null);
      setProcessingRequest(notificationId || requestId);

      const actionType = action === 'approve' ? 'accepted' : 'rejected';
      await petGuardianshipAPI.respondToRequest(requestId, actionType);

      // Update local state - mark as processed
      setNotifications(prevNotifications => {
        return prevNotifications.map(notif => {
          if (notif.id === notificationId) {
            return {
              ...notif,
              processed: true,
              processedAction: action,
              mainText: action === 'approve'
                ? `Guardianship invitation accepted`
                : `Guardianship invitation rejected`
            };
          }
          return notif;
        });
      });

      // Remove processed notification after showing the status for 2 seconds
      setTimeout(() => {
        setNotifications(current => current.filter(notif => notif.id !== notificationId));
      }, 2000);

      // Show success toast
      showSuccessToast(action === "approve" ? "Successfully joined as a guardian!" : "Invitation declined.");

      if (onRequestHandled) {
        onRequestHandled();
      }
    } catch (error) {
      console.error(`Error ${action}ing guardianship invite:`, error);
      showErrorToast(`Failed to ${action} guardianship invite`);
    } finally {
      setProcessingRequest(null);
    }
  };

  const fetchNotifications = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Use per_page: 20 for dropdown to show more items
      const response = await notificationsAPI.getNotificationsWithRead('drawer', { per_page: 20 });
      const result = response.data;

      // Extract pending follow requests from the new structure
      if (result.follow_requests && result.follow_requests.data && Array.isArray(result.follow_requests.data.requesters)) {
        setPendingFollowRequests(result.follow_requests.data.requesters);
      } else {
        setPendingFollowRequests([]);
      }

      // Extract the notifications array from the response
      // API response structure: { data: [...], links: {...}, meta: {...} }
      const notificationsArray = result?.data || result || [];


      // Parse notifications using the service
      const parsedNotifications = notificationService.parseNotifications(notificationsArray);


      setNotifications(parsedNotifications);
    } catch (error) {
      console.error('❌ Error fetching notifications:', error);
      setError(error.message || 'Failed to fetch notifications');
      handleApiError(error, setError);
    } finally {
      setIsLoading(false);
    }
  };

  // Start polling for notifications
  useEffect(() => {
    if (isOpen) {
      // Reset notification badge count to 0 when dropdown opens
      if (updateNotificationCount) {
        updateNotificationCount(0);
      } else if (onRequestHandled) {
        // Fallback: trigger parent component to refresh the count
        onRequestHandled();
      }

      fetchNotifications(); // Fetch notifications when dropdown opens
    }

    // Cleanup interval when component unmounts or dropdown closes
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isOpen, onRequestHandled, updateNotificationCount]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Scroll highlighted notification into view when dropdown opens
  useEffect(() => {
    if (isOpen && highlightNotificationId && highlightedNotificationRef.current) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        if (highlightedNotificationRef.current) {
          highlightedNotificationRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }, 300);
    }
  }, [isOpen, highlightNotificationId, notifications]);

  // Remove highlight after 5 seconds or when user interacts
  useEffect(() => {
    if (highlightNotificationId) {
      const timeout = setTimeout(() => {
        // Highlight will be cleared when dropdown closes or user interacts
        // This is just a fallback timeout
      }, 5000);

      return () => clearTimeout(timeout);
    }
  }, [highlightNotificationId]);

  if (!isOpen) return null;

  return (
    <div
      className="notification-dropdown"
      style={{
        '--notification-dropdown-primary-color': colors.primary,
        '--notification-dropdown-hover-color': colors.hover,
        '--notification-dropdown-profile-bg': colors.profileBg,
      }}
    >
      {/* Header */}
      <div className="notification-dropdown-header">
        <h3>{t("navbar.Notifications")}</h3>
        <button onClick={onClose} className="notification-dropdown-close-btn">
          ×
        </button>
      </div>

      {/* Content */}
      <div className="notification-dropdown-content">
        {isLoading ? (
          <div className="notification-dropdown-loading">
            {t("navbar.Loading_Notifications")}
          </div>
        ) : error ? (
          <div className="notification-dropdown-error">
            {error}
          </div>
        ) : pendingFollowRequests.length === 0 && notifications.length === 0 ? (
          <div className="notification-dropdown-empty">
            {t("navbar.No_New_Notifications")}
          </div>
        ) : (
          <div>
            {/* Pending Follow Requests Section */}
            {pendingFollowRequests.length > 0 && (
              <div>
                <div className="notification-dropdown-section-header">
                  {t("navbar.Pending_Follow_Requests")} (
                  {pendingFollowRequests.length})
                </div>

                {pendingFollowRequests.map((request, index) => (
                  <div
                    key={request.user_id || index}
                    className="notification-dropdown-follow-request-item"
                  >
                    {/* User Avatar */}
                    <div className="notification-dropdown-avatar">
                      {request.profile_image_url ? (
                        <img
                          src={request.profile_image_url}
                          alt="Profile"
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextSibling.style.display = "flex";
                          }}
                        />
                      ) : null}
                      <div
                        style={{
                          display: request.profile_image_url ? "none" : "flex",
                        }}
                      >
                        {request.username
                          ? request.username
                            .split("@")[0]
                            .split(".")
                            .map((n) => n[0])
                            .join("")
                            .slice(0, 2)
                            .toUpperCase()
                          : "U"}
                      </div>
                    </div>

                    {/* User Info */}
                    <div className="notification-dropdown-user-info">
                      <div
                        className="notification-dropdown-user-name"
                        onClick={() => {
                          // Navigate to user profile page
                          navigate(
                            `/social/profile/${request.user_id || request.id || request.username
                            }`
                          );
                          if (onClose) onClose();
                        }}
                        title="View profile"
                      >
                        {request.first_name}
                      </div>
                      <div className="notification-dropdown-user-description">
                        sent you a buddy request
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="notification-dropdown-actions">
                      <button
                        onClick={() =>
                          handleFollowRequest(request.user_id || request.id, "approve")
                        }
                        className="notification-dropdown-btn notification-dropdown-btn-primary notification-dropdown-btn-icon"
                        title={t("Accept")}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </button>
                      <button
                        onClick={() =>
                          handleFollowRequest(request.user_id || request.id, "reject")
                        }
                        className="notification-dropdown-btn notification-dropdown-btn-secondary notification-dropdown-btn-icon"
                        title={t("Reject")}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                      <button
                        onClick={() => {
                          navigate(`/social/profile/${request.user_id || request.id || request.username}`);
                          if (onClose) onClose();
                        }}
                        className="notification-dropdown-btn notification-dropdown-btn-view notification-dropdown-btn-icon"
                        title={t("View_Profile")}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="8" r="4"></circle>
                          <path d="M4 20c0-4 4-6 8-6s8 2 8 6"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Other Notifications Section */}
            {notifications.length > 0 && (
              <div>
                <div className="notification-dropdown-section-header">
                  {t("navbar.Other_Notifications")} ({notifications.length})
                </div>

                {notifications.map((notification, index) => {
                  // Use parsed data from the fetch function
                  const parsedData = notification.parsedData || {};
                  const notificationType = notification.type;

                  // Get notification icon based on type and reaction type
                  const isChannelNotification = (type) => type?.startsWith("channel_");
                  const getNotificationIcon = (type, data = {}) => {
                    if (notification.getIcon && isChannelNotification(type)) {
                      return notification.getIcon();
                    }
                    switch (type) {
                      case "appointment_created":
                      case "appointment_updated":
                      case "appointment_canceled":
                      case "appointment_restored":
                      case "appointment_status_changed":
                        return "📅";
                      case "outpatient_visit_created":
                      case "outpatient_visit_updated":
                      case "outpatient_visit_status_changed":
                        return "🏥";
                      case "inpatient_visit_created":
                      case "inpatient_visit_updated":
                      case "inpatient_visit_status_changed":
                        return "🏨";
                      case "reaction":
                      case "post_reaction":
                      case "story_reaction":
                        // Get the actual reaction type from the notification data
                        const reactionType = data.reaction_type;
                        // Map reaction types to emojis
                        // Custom emoji image paths
                        const HappyEmoji = "/icons/Reactions/Sulhafa_Emoji_Happy.png";
                        const LoveEmoji = "/icons/Reactions/Sulhafa_Emoji_Love.png";
                        const LaughEmoji = "/icons/Reactions/Sulhafa_Emoji_Laugh.png";
                        const ShockedEmoji = "/icons/Reactions/Sulhafa_Emoji_Shocked.png";
                        const SadEmoji = "/icons/Reactions/Sulhafa_Emoji_Sad.png";
                        const AngryEmoji = "/icons/Reactions/Sulhafa_Emoji_Angry.png";
                        const reactionTypeToEmoji = {
                          'like': HappyEmoji,
                          'love': LoveEmoji,
                          'funny': LaughEmoji,
                          'haha': LaughEmoji,
                          'laugh': LaughEmoji,
                          'wow': ShockedEmoji,
                          'sad': SadEmoji,
                          'angry': AngryEmoji
                        };
                        // Return the specific reaction emoji or default to happy
                        return reactionTypeToEmoji[reactionType] || HappyEmoji;
                      case "new_follow_request":
                        return "👥";
                      case "comment":
                        return "💬";
                      case "repost":
                        return "🔁";
                      case "like":
                        return "❤️";
                      case "clinic_invitation":
                        return "🏥";
                      case "guardianship_invitation":
                      case "guardianship_response":
                        return "🐾";
                      case "new_user_same_city":
                        return "👤";
                      default:
                        return "🔔";
                    }
                  };

                  // Get the best available message
                  const getMessage = () => {
                    if (parsedData.content) return parsedData.content;
                    if (parsedData.message) return parsedData.message;
                    if (notification.message) return notification.message;
                    return "New notification";
                  };

                  // Get the best available title
                  const getTitle = () => {
                    if (parsedData.title) return parsedData.title;
                    if (notification.title) return notification.title;
                    return "Notification";
                  };

                  // Get sender info if available
                  const getSenderInfo = () => {
                    if (parsedData.sender_name) return parsedData.sender_name;
                    if (parsedData.sender?.name) return parsedData.sender.name;
                    if (parsedData.user_name) return parsedData.user_name;
                    if (notification.sender_Data?.name) return notification.sender_Data.name;
                    return null;
                  };

                  // Get sender image
                  const getSenderImage = () => {
                    // Check notification.sender_Data first (from backend NotificationResource)
                    const senderDataImage = notification.sender_Data?.image;
                    if (senderDataImage && typeof senderDataImage === 'string' && senderDataImage.trim() !== '') {
                      return senderDataImage;
                    }
                    // Check parsedData for sender image
                    const parsedSenderImage = parsedData.sender_image;
                    if (parsedSenderImage && typeof parsedSenderImage === 'string' && parsedSenderImage.trim() !== '') {
                      return parsedSenderImage;
                    }
                    const parsedSenderObjImage = parsedData.sender?.image;
                    if (parsedSenderObjImage && typeof parsedSenderObjImage === 'string' && parsedSenderObjImage.trim() !== '') {
                      return parsedSenderObjImage;
                    }
                    // Check nested data
                    const nestedSenderImage = parsedData.data?.sender?.image;
                    if (nestedSenderImage && typeof nestedSenderImage === 'string' && nestedSenderImage.trim() !== '') {
                      return nestedSenderImage;
                    }
                    return null;
                  };

                  // Get notification main text (sender name + action)
                  const getNotificationMainText = () => {
                    if (notification.getMainText && isChannelNotification(notificationType)) {
                      return notification.getMainText();
                    }

                    const senderName = getSenderInfo() || 'Someone';

                    switch (notificationType) {
                      case "reaction":
                      case "post_reaction":
                      case "story_reaction":
                        const reactionType = parsedData.reaction_type || 'like';
                        const reactionLabels = {
                          'like': 'liked',
                          'love': 'loved',
                          'funny': 'laughed at',
                          'haha': 'laughed at',
                          'laugh': 'laughed at',
                          'wow': 'was surprised by',
                          'sad': 'was sad about',
                          'angry': 'was angry about'
                        };
                        const action = reactionLabels[reactionType] || 'reacted to';
                        const targetStr = parsedData.content_type?.includes('Story') ? 'story' : 'post';
                        return `${senderName} ${action} your ${targetStr}`;

                      case "appointment_created":
                        const petName = parsedData.pet_name;
                        return petName
                          ? `${senderName} created an appointment for ${petName}`
                          : `${senderName} created an appointment for your pet`;
                      case "appointment_updated":
                        return parsedData.pet_name
                          ? `${senderName} updated ${parsedData.pet_name}'s appointment`
                          : `${senderName} updated an appointment`;
                      case "appointment_canceled":
                        return parsedData.pet_name
                          ? `${senderName} canceled ${parsedData.pet_name}'s appointment`
                          : `${senderName} canceled an appointment`;
                      case "appointment_restored":
                        return parsedData.pet_name
                          ? `${senderName} restored ${parsedData.pet_name}'s appointment`
                          : `${senderName} restored an appointment`;
                      case "appointment_status_changed":
                        return parsedData.pet_name
                          ? `${senderName} changed ${parsedData.pet_name}'s appointment status`
                          : `${senderName} changed appointment status`;

                      case "outpatient_visit_created":
                        return parsedData.pet_name
                          ? `${senderName} created an outpatient visit for ${parsedData.pet_name}`
                          : `${senderName} created an outpatient visit`;
                      case "outpatient_visit_updated":
                        return parsedData.pet_name
                          ? `${senderName} updated ${parsedData.pet_name}'s outpatient visit`
                          : `${senderName} updated an outpatient visit`;
                      case "outpatient_visit_status_changed":
                        return parsedData.pet_name
                          ? `${senderName} changed ${parsedData.pet_name}'s outpatient visit status`
                          : `${senderName} changed outpatient visit status`;

                      case "inpatient_visit_created":
                        return parsedData.pet_name
                          ? `${senderName} created an inpatient visit for ${parsedData.pet_name}`
                          : `${senderName} created an inpatient visit`;
                      case "inpatient_visit_updated":
                        return parsedData.pet_name
                          ? `${senderName} updated ${parsedData.pet_name}'s inpatient visit`
                          : `${senderName} updated an inpatient visit`;
                      case "inpatient_visit_status_changed":
                        return parsedData.pet_name
                          ? `${senderName} changed ${parsedData.pet_name}'s inpatient visit status`
                          : `${senderName} changed inpatient visit status`;

                      case "comment":
                        return `${senderName} commented on your post`;
                      case "repost":
                        return `${senderName} reposted your post`;

                      case "new_follow_request":
                        return `${senderName} sent you a buddy request`;

                      case "clinic_invitation":
                        return parsedData.clinic_name
                          ? `${senderName} invited you to join ${parsedData.clinic_name}`
                          : `${senderName} invited you to join a clinic`;

                      case "guardianship_invitation":
                        return `${senderName} invited you to become a guardian for ${parsedData.pet_name || 'their pet'}`;

                      case "guardianship_response":
                        const status = parsedData.status === 'accepted' ? 'accepted' : 'refused';
                        return `${senderName} has ${status} your guardianship invitation for ${parsedData.pet_name || 'your pet'}`;

                      case "new_user_same_city":
                        const cityName = parsedData.city_name || 'your city';
                        return `${senderName} just joined Sulhafa from ${cityName}`;

                      default:
                        // Fallback to existing message
                        const message = getMessage();
                        if (message && message !== 'New notification') {
                          return message;
                        }
                        return `${senderName} sent you a notification`;
                    }
                  };

                  // Get additional helpful details for notifications
                  const getNotificationDetails = () => {
                    if (notification.getDetails && isChannelNotification(notificationType)) {
                      return notification.getDetails();
                    }

                    const details = [];

                    switch (notificationType) {
                      case "appointment_created":
                      case "appointment_updated":
                      case "appointment_canceled":
                      case "appointment_restored":
                      case "appointment_status_changed":
                        if (parsedData.pet_name) {
                          details.push(`Pet: ${parsedData.pet_name}`);
                        }
                        if (parsedData.clinic_name) {
                          details.push(`Clinic: ${parsedData.clinic_name}`);
                        }
                        if (parsedData.appointment_date) {
                          try {
                            const date = new Date(parsedData.appointment_date);
                            if (!isNaN(date.getTime())) {
                              const formattedDate = date.toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              });
                              const formattedTime = date.toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit'
                              });
                              details.push(`${formattedDate} at ${formattedTime}`);
                            }
                          } catch (e) {
                            // Ignore date parsing errors
                          }
                        }
                        if (parsedData.status) {
                          details.push(`Status: ${parsedData.status}`);
                        }
                        break;

                      case "outpatient_visit_created":
                      case "outpatient_visit_updated":
                      case "outpatient_visit_status_changed":
                        if (parsedData.out_serial_num) {
                          details.push(`Serial: ${parsedData.out_serial_num}`);
                        }
                        if (parsedData.pet_name) {
                          details.push(`Pet: ${parsedData.pet_name}`);
                        }
                        if (parsedData.clinic_name) {
                          details.push(`Clinic: ${parsedData.clinic_name}`);
                        }
                        if (parsedData.visit_date) {
                          try {
                            const date = new Date(parsedData.visit_date);
                            if (!isNaN(date.getTime())) {
                              const formattedDate = date.toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              });
                              details.push(formattedDate);
                            }
                          } catch (e) {
                            // Ignore date parsing errors
                          }
                        }
                        break;

                      case "inpatient_visit_created":
                      case "inpatient_visit_updated":
                      case "inpatient_visit_status_changed":
                        if (parsedData.in_serial_num) {
                          details.push(`Serial: ${parsedData.in_serial_num}`);
                        }
                        if (parsedData.pet_name) {
                          details.push(`Pet: ${parsedData.pet_name}`);
                        }
                        if (parsedData.clinic_name) {
                          details.push(`Clinic: ${parsedData.clinic_name}`);
                        }
                        if (parsedData.visit_date) {
                          try {
                            const date = new Date(parsedData.visit_date);
                            if (!isNaN(date.getTime())) {
                              const formattedDate = date.toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              });
                              details.push(formattedDate);
                            }
                          } catch (e) {
                            // Ignore date parsing errors
                          }
                        }
                        break;

                      case "clinic_invitation":
                        if (parsedData.clinic_name) {
                          details.push(`Clinic: ${parsedData.clinic_name}`);
                        }
                        if (parsedData.clinic_name_ar) {
                          details.push(`العيادة: ${parsedData.clinic_name_ar}`);
                        }
                        break;

                      case "reaction":
                      case "post_reaction":
                      case "story_reaction":
                        // Show reaction type emoji if available
                        const reactionType = parsedData.reaction_type || 'like';
                        // Custom emoji image paths
                        const HappyEmoji = "/icons/Reactions/Sulhafa_Emoji_Happy.png";
                        const LoveEmoji = "/icons/Reactions/Sulhafa_Emoji_Love.png";
                        const LaughEmoji = "/icons/Reactions/Sulhafa_Emoji_Laugh.png";
                        const ShockedEmoji = "/icons/Reactions/Sulhafa_Emoji_Shocked.png";
                        const SadEmoji = "/icons/Reactions/Sulhafa_Emoji_Sad.png";
                        const AngryEmoji = "/icons/Reactions/Sulhafa_Emoji_Angry.png";
                        const reactionEmojis = {
                          'like': HappyEmoji,
                          'love': LoveEmoji,
                          'funny': LaughEmoji,
                          'haha': LaughEmoji,
                          'laugh': LaughEmoji,
                          'wow': ShockedEmoji,
                          'sad': SadEmoji,
                          'angry': AngryEmoji
                        };
                        const emoji = reactionEmojis[reactionType] || HappyEmoji;
                        // Store emoji path as object so we can render it as image
                        const isStoryReact = parsedData.content_type?.includes('Story');
                        if (!isStoryReact) {
                          details.push({ type: 'reaction', emojiPath: emoji });
                        }
                        break;

                      case "comment":
                        // Show comment preview if available
                        if (parsedData.comment_preview || parsedData.comment_content) {
                          const preview = (parsedData.comment_preview || parsedData.comment_content || '').substring(0, 50);
                          if (preview) {
                            details.push(`"${preview}${preview.length >= 50 ? '...' : ''}"`);
                          }
                        }
                        break;

                      case "new_user_same_city":
                        // Show city name if available
                        if (parsedData.city_name) {
                          details.push(`City: ${parsedData.city_name}`);
                        }
                        break;
                    }

                    return details;
                  };

                  // Get content thumbnail image
                  const getContentThumbnail = () => {
                    // For reactions and comments, try to get post image
                    if (notificationType === "reaction" || notificationType === "post_reaction" || notificationType === "story_reaction" || notificationType === "comment") {
                      if (parsedData.post_image) return parsedData.post_image;
                      if (parsedData.image) return parsedData.image;
                      if (parsedData.media?.[0]?.url) return parsedData.media[0].url;
                      if (parsedData.data?.post_image) return parsedData.data.post_image;
                      if (parsedData.data?.image) return parsedData.data.image;
                    }

                    // For appointments and visits, try to get pet image
                    if (notificationType.includes('appointment') ||
                      notificationType.includes('outpatient') ||
                      notificationType.includes('inpatient')) {
                      if (parsedData.pet_image) return parsedData.pet_image;
                      if (parsedData.image) return parsedData.image;
                      if (parsedData.data?.pet_image) return parsedData.data.pet_image;
                    }

                    // Generic image fallback
                    if (parsedData.image && typeof parsedData.image === 'string' && parsedData.image.trim() !== '') {
                      return parsedData.image;
                    }
                    if (notification.image && typeof notification.image === 'string' && notification.image.trim() !== '') {
                      return notification.image;
                    }

                    return null;
                  };

                  // Handle notification click based on type - using centralized routing utility
                  const handleNotificationClick = async () => {
                    // Don't handle click for notifications that aren't clickable (have their own buttons)
                    if (!isNotificationClickable(notificationType)) {
                      return;
                    }

                    // Use centralized routing utility
                    handleNotificationRouting(
                      notification,
                      navigate,
                      onClose,
                      openPostModal
                    );
                  };

                  const senderImage = getSenderImage();
                  const contentThumbnail = getContentThumbnail();
                  const isUnread = notification.status === 'unread' || !notification.is_read;

                  // Check if this notification should be highlighted
                  // Try multiple ID fields to match with highlightNotificationId
                  const notificationId = notification.id ||
                    notification.notification_id ||
                    notification.notificationId ||
                    notification.data?.id ||
                    notification.data?.notification_id;

                  const isHighlighted = highlightNotificationId && notificationId &&
                    (notificationId.toString() === highlightNotificationId.toString() ||
                      notification.id?.toString() === highlightNotificationId.toString() ||
                      notification.notification_id?.toString() === highlightNotificationId.toString());

                  return (
                    <div
                      key={notification.id || index}
                      ref={isHighlighted ? highlightedNotificationRef : null}
                      className={`notification-dropdown-item ${isUnread ? 'notification-dropdown-item-unread' : ''} ${isHighlighted ? 'notification-dropdown-item-highlighted' : ''}`}
                      onClick={handleNotificationClick}
                      style={{ cursor: 'pointer' }}
                    >
                      {/* Left blue bar for unread notifications */}
                      {isUnread && <div className="notification-dropdown-unread-bar" />}

                      {/* Sender profile picture */}
                      <div className="notification-dropdown-avatar-main">
                        {senderImage ? (
                          <img
                            src={senderImage}
                            alt={getSenderInfo() || "Sender"}
                            style={{
                              border: `2px solid ${getColorScheme(notification.sender_Data?.user_type || parsedData.sender_Data?.user_type || parsedData.user_data?.user_type || parsedData.user?.user_type).primary}`,
                              borderRadius: "50%"
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              if (e.target.nextSibling) {
                                e.target.nextSibling.style.display = 'flex';
                              }
                            }}
                          />
                        ) : null}
                        <div
                          className="notification-dropdown-avatar-fallback"
                          style={{ 
                            display: senderImage ? 'none' : 'flex',
                            border: `2px solid ${getColorScheme(notification.sender_Data?.user_type || parsedData.sender_Data?.user_type || parsedData.user_data?.user_type || parsedData.user?.user_type).primary}`,
                            background: getColorScheme(notification.sender_Data?.user_type || parsedData.sender_Data?.user_type || parsedData.user_data?.user_type || parsedData.user?.user_type).primary
                          }}
                        >
                          {getSenderInfo() ? getSenderInfo().charAt(0).toUpperCase() : '?'}
                        </div>
                      </div>

                      {/* Main content area */}
                      <div className="notification-dropdown-item-content-main">
                        <div className="notification-dropdown-main-text">
                          {notification.processed ? notification.mainText : getNotificationMainText()}
                        </div>

                        {/* Additional helpful details */}
                        {(() => {
                          const details = getNotificationDetails();
                          if (details.length > 0) {
                            return (
                              <div className="notification-dropdown-details">
                                {details.map((detail, idx) => (
                                  <span key={idx} className="notification-dropdown-detail-item">
                                    {typeof detail === 'object' && (detail.type === 'reaction' || detail.type === 'post_reaction' || detail.type === 'story_reaction') ? (
                                      <>
                                        <span>Reaction: </span>
                                        <img
                                          src={detail.emojiPath}
                                          alt="Reaction"
                                          style={{ width: '28px', height: '28px', verticalAlign: 'middle', marginLeft: '4px' }}
                                        />
                                      </>
                                    ) : (
                                      detail
                                    )}
                                    {idx < details.length - 1 && <span className="notification-dropdown-detail-separator"> • </span>}
                                  </span>
                                ))}
                              </div>
                            );
                          }
                          return null;
                        })()}

                        <div className="notification-dropdown-time-main">
                          {notification.time_ago || "Just now"}
                        </div>

                        {/* Action buttons for clinic invitations */}
                        {notificationType === "clinic_invitation" && (
                          <div
                            className="notification-dropdown-clinic-actions"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const invitationId =
                                  parsedData.invitation_id ||
                                  notification.target_id ||
                                  notification.id;
                                handleClinicInvitation(invitationId, "accept", notification.id);
                              }}
                              disabled={processingRequest === notification.id}
                              className="notification-dropdown-btn-small notification-dropdown-btn-small-primary"
                            >
                              {processingRequest === notification.id ? t("navbar.Processing") : t("navbar.Accept")}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const invitationId =
                                  parsedData.invitation_id ||
                                  notification.target_id ||
                                  notification.id;
                                handleClinicInvitation(invitationId, "reject", notification.id);
                              }}
                              disabled={processingRequest === notification.id}
                              className="notification-dropdown-btn-small notification-dropdown-btn-small-secondary"
                            >
                              {processingRequest === notification.id ? t("navbar.Processing") : t("navbar.Reject")}
                            </button>
                            <button
                              onClick={() => {
                                navigate("/medical/manage-team");
                                onClose();
                              }}
                              className="notification-dropdown-btn-small notification-dropdown-btn-small-blue"
                            >
                              {t("navbar.View")}
                            </button>
                          </div>
                        )}

                        {/* Action buttons for connection requests */}
                        {(notificationType === "connection_request" || notificationType === "new_follow_request") && !notification.processed && (
                          <div
                            className="notification-dropdown-clinic-actions"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                // Get sender ID from various possible locations
                                const senderId =
                                  notification.sender_id ||
                                  parsedData.sender_id ||
                                  parsedData.user_id ||
                                  notification.sender_Data?.id ||
                                  parsedData.content_id ||
                                  notification.target_id;



                                if (senderId) {
                                  await handleFollowRequest(senderId, "approve", notification.id);
                                }
                              }}
                              disabled={processingRequest === notification.id}
                              className="notification-dropdown-btn-small notification-dropdown-btn-small-primary"
                            >
                              {processingRequest === notification.id ? t("navbar.Processing") : t("navbar.Accept")}
                            </button>
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                // Get sender ID from various possible locations
                                const senderId =
                                  notification.sender_id ||
                                  parsedData.sender_id ||
                                  parsedData.user_id ||
                                  notification.sender_Data?.id ||
                                  parsedData.content_id ||
                                  notification.target_id;

                                if (senderId) {
                                  await handleFollowRequest(senderId, "reject", notification.id);
                                }
                              }}
                              disabled={processingRequest === notification.id}
                              className="notification-dropdown-btn-small notification-dropdown-btn-small-secondary"
                            >
                              {processingRequest === notification.id ? t("navbar.Processing") : t("navbar.Reject")}
                            </button>
                            <button
                              onClick={() => {
                                // Get sender ID from various possible locations
                                const senderId =
                                  notification.sender_id ||
                                  parsedData.sender_id ||
                                  parsedData.user_id ||
                                  notification.sender_Data?.id ||
                                  parsedData.content_id ||
                                  notification.target_id;

                                if (senderId) {
                                  navigate(`/social/profile/${senderId}`);
                                  onClose();
                                }
                              }}
                              className="notification-dropdown-btn-small notification-dropdown-btn-small-blue"
                            >
                              {t("navbar.View_Profile")}
                            </button>
                          </div>
                        )}

                        {/* Action buttons for guardianship invitations */}
                        {notificationType === "guardianship_invitation" && !notification.processed && (
                          <div
                            className="notification-dropdown-clinic-actions"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                const requestId = parsedData.request_id || notification.target_id;
                                if (requestId) {
                                  await handleGuardianshipResponse(requestId, "approve", notification.id);
                                }
                              }}
                              disabled={processingRequest === notification.id}
                              className="notification-dropdown-btn-small notification-dropdown-btn-small-primary"
                            >
                              {processingRequest === notification.id ? "Processing..." : "Accept"}
                            </button>
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                const requestId = parsedData.request_id || notification.target_id;
                                if (requestId) {
                                  await handleGuardianshipResponse(requestId, "reject", notification.id);
                                }
                              }}
                              disabled={processingRequest === notification.id}
                              className="notification-dropdown-btn-small notification-dropdown-btn-small-secondary"
                            >
                              {processingRequest === notification.id ? "Processing..." : "Reject"}
                            </button>
                            <button
                              onClick={() => {
                                const petId = parsedData.pet_id;
                                if (petId) {
                                  navigate(`/pets/${petId}/view`);
                                  onClose();
                                }
                              }}
                              className="notification-dropdown-btn-small notification-dropdown-btn-small-primary"
                            >
                              View Pet
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Content thumbnail on the right */}
                      {contentThumbnail && (
                        <div className="notification-dropdown-thumbnail">
                          <img
                            src={contentThumbnail}
                            alt="Content preview"
                            onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="notification-dropdown-footer">
        <button
          onClick={() => {
            // Map userType to route format (e.g., PET_GUARDIAN -> pet-guardian)
            const userTypeRoute = userType?.toLowerCase()?.replace(/_/g, '-') || 'pet-guardian';
            // Try user-specific route first, fallback to general route
            const route = `/dashboard/${userTypeRoute}/notifications`;
            navigate(route);
            onClose();
          }}
          className="notification-dropdown-footer-btn"
        >
          {t("navbar.View_Notifications")}
        </button>
      </div>
    </div>
  );
};
export default NotificationDropdown;
